/**
 * The console's session, in the browser.
 *
 * Handover rev7 §11.12.1: sign-in is Google, through Supabase Auth. In local
 * and test there is also the seeded session of the three test staff, which
 * `public.staff_sign_in_seeded` refuses outright when the database or APP_ENV
 * says production.
 *
 * Whichever it is, the session asserts **who** and nothing else. Every role
 * decision is the server's: the menu comes back from `console_menu`, each
 * view's data comes back from a `console_*` function that re-reads the roles
 * out of `public.staff`, and a view this browser decided to show without the
 * role behind it answers 403. So nothing here is a security boundary, and
 * nothing here is written as though it were.
 *
 * `sessionStorage`, not `localStorage`: a console session ends with the tab.
 */

export const SESSION_HEADER = 'x-plt-console-session'
const STORE_KEY = 'plt.console.session'

export interface ConsoleStaff {
  kind: 'staff'
  email: string
  name: string
  roles: string[]
  display_name?: string | null
}

export interface ConsoleView {
  key: string
  label: string
  path: string
  built: boolean
  built_by: string | null
  note: string | null
}

export interface StoredSession {
  provider: 'google' | 'seeded'
  staff: ConsoleStaff
  views: ConsoleView[]
  appEnv: string | null
  /** The seeded session's signed token. A Google session carries its access
   *  token on `Authorization` instead and does not store one here. */
  token?: string
  expiresAt?: string
  /** A Google session's Supabase access token. */
  accessToken?: string
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function store(): Store | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    // A browser with storage blocked still gets a console; it just signs in
    // again on the next page load.
    return null
  }
}

export function readSession(now: Date = new Date(), from: Store | null = store()): StoredSession | null {
  const raw = from?.getItem(STORE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredSession
    if (parsed.expiresAt !== undefined && new Date(parsed.expiresAt).getTime() <= now.getTime()) {
      from?.removeItem(STORE_KEY)
      return null
    }
    if (!parsed.staff?.email) return null
    return parsed
  } catch {
    return null
  }
}

export function writeSession(session: StoredSession, to: Store | null = store()): void {
  to?.setItem(STORE_KEY, JSON.stringify(session))
}

export function clearSession(to: Store | null = store()): void {
  to?.removeItem(STORE_KEY)
}

/** The headers one console call needs on top of the publishable key. */
export function sessionHeaders(session: StoredSession | null): Record<string, string> {
  if (session === null) return {}
  if (session.provider === 'seeded' && session.token !== undefined) {
    return { [SESSION_HEADER]: session.token }
  }
  return {}
}

/**
 * Whether this browser should show the seeded sign-in at all.
 *
 * Keyed on the project the bundle talks to, NOT on `APP_ENV`. `APP_ENV` is a
 * label, it is on the public allow-list, and a deployed test build carries
 * `test` — so the old `appEnv === 'local' || appEnv === 'test'` put the three
 * buttons and the banner on the deployed test page, on the open internet, for
 * anyone with the address. A loopback host cannot be a deployed project: it is
 * the one thing about a bundle that a hosted build can never accidentally
 * satisfy.
 *
 * This is tidiness, not the guard. `public.staff_sign_in_seeded` refuses
 * anywhere `public.is_local_stack()` is not true (20261006160000), and that
 * refusal is the server's and cannot be reached from here.
 */
export function seededLoginOffered(supabaseUrl: string): boolean {
  let host: string
  try {
    host = new URL(supabaseUrl).hostname.toLowerCase()
  } catch {
    // Not a URL at all: nothing to prove local with, so offer nothing.
    return false
  }
  // IPv6 loopback arrives bracketed from URL.hostname on some engines.
  const bare = host.replace(/^\[|\]$/g, '')
  return bare === 'localhost' || bare === '127.0.0.1' || bare === '::1'
}

/** The three seeded test staff of `supabase/seed/staff.csv`. Addresses only —
 *  the names, the roles and whether each is still active are the server's. */
export const SEEDED_STAFF = [
  { email: 'admin@example.com', hint: 'admin' },
  { email: 'catering@example.com', hint: 'catering' },
  { email: 'kitchen@example.com', hint: 'kitchen · allergen_signatory · payments' },
] as const

/* ------------------------------------------------------------------ *
 * Run 12 item G (DECIDED 20261012): the page a staff member was sent to.
 *
 * A staff email links to an order's page or to a view. Signed out, the
 * console shows the sign-in on that same address; the seeded sign-in keeps
 * the address, but Google's round trip comes back to `/staff`, which Supabase
 * Auth's redirect list allows. So the page is remembered before leaving and
 * taken up again on the way back. Only a console path is accepted: anything
 * else (another origin, `//host`, the customer page) falls back to `/staff`.
 * ------------------------------------------------------------------ */

const RETURN_KEY = 'plt.console.return_to'

/** A console path, or null: `/staff` and below, same origin, nothing else. */
export function consoleReturnPath(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  if (!/^\/staff(?:[/?#]|$)/.test(value)) return null
  if (value.startsWith('//') || value.includes('\\') || /[\u0000-\u001f]/.test(value)) return null
  return value
}

export function rememberReturnPath(path: string, to: Store | null = store()): void {
  const safe = consoleReturnPath(path)
  if (safe === null || safe === '/staff' || safe === '/staff/') to?.removeItem(RETURN_KEY)
  else to?.setItem(RETURN_KEY, safe)
}

/** The remembered page, once: reading it forgets it. */
export function takeReturnPath(from: Store | null = store()): string {
  const raw = from?.getItem(RETURN_KEY) ?? null
  from?.removeItem(RETURN_KEY)
  return consoleReturnPath(raw) ?? '/staff'
}
